import {
  assertPlayback,
  cyclePlaybackSession,
  recoverPlaybackSession,
} from "./audio_session.js"

export const PING_PERIOD_MS = 8_000
export const DRIFT_OK_S = 0.08
export const DRIFT_NUDGE_S = 0.35

export function positionMs(serverNowMs, durationMs, originMs = 0) {
  const duration = Number(durationMs)
  if (!Number.isFinite(duration) || duration <= 0) return 0
  const rem = (Number(serverNowMs) - originMs) % duration
  return rem < 0 ? rem + duration : rem
}

export function wrapError(err, duration) {
  if (!Number.isFinite(duration) || duration <= 0) return err
  const half = duration / 2
  return ((((err + half) % duration) + duration) % duration) - half
}

export function mediaUrl(url, version) {
  if (!url) return url
  if (version == null || version === "") return url
  const sep = String(url).includes("?") ? "&" : "?"
  return `${url}${sep}v=${encodeURIComponent(version)}`
}

export function publicEventSlug(pathname) {
  const parts = String(pathname || "").split("/")
  if (parts[1] === "e" && parts[2]) {
    try {
      return decodeURIComponent(parts[2])
    } catch {
      return parts[2]
    }
  }
  return null
}

function muteKey(slug) {
  return `moon_scan_audio_mute:${slug}`
}

function muteStore() {
  try {
    return window.sessionStorage
  } catch {
    return null
  }
}

function readMuted(slug) {
  if (!slug) return false
  try {
    return muteStore()?.getItem(muteKey(slug)) === "1"
  } catch {
    return false
  }
}

function writeMuted(slug, muted) {
  if (!slug) return
  try {
    muteStore()?.setItem(muteKey(slug), muted ? "1" : "0")
  } catch {
    /* ignore quota / private mode */
  }
}

export function chipView({session, unlocked, muted, playing, ctxState, gestureArmed}) {
  const running = ctxState === "running"
  const audible = Boolean(playing) && running && unlocked && !muted
  const waiting =
    Boolean(session) && !audible && !muted && (!unlocked || !running || Boolean(gestureArmed))
  const show = Boolean(session) && (unlocked || playing || waiting || session.surface === "mosaic")
  let label = "Mute campaign music"
  if (waiting) label = "Play campaign music"
  else if (muted) label = "Unmute campaign music"
  return {
    show,
    waiting,
    muted: Boolean(muted) && !waiting,
    audible,
    label,
    pressed: Boolean(muted) && !waiting,
  }
}

function decodeAudioData(ctx, bytes) {
  return new Promise((resolve, reject) => {
    let settled = false
    const ok = (buf) => {
      if (settled) return
      settled = true
      resolve(buf)
    }
    const fail = (err) => {
      if (settled) return
      settled = true
      reject(err || new Error("decodeAudioData failed"))
    }
    try {
      const maybe = ctx.decodeAudioData(bytes.slice(0), ok, fail)
      if (maybe && typeof maybe.then === "function") maybe.then(ok, fail)
    } catch (err) {
      fail(err)
    }
  })
}

function newContext() {
  const Ctor = window.AudioContext || window.webkitAudioContext
  return Ctor ? new Ctor() : null
}

const CampaignAudio = {
  lv: null,
  session: null,
  ctx: null,
  gain: null,
  source: null,
  buffer: null,
  bytes: null,
  loadKey: "",
  unlocked: false,
  muted: false,
  playing: false,
  gen: 0,
  samples: [],
  clockOffset: null,
  _stopTimer: 0,
  _pingTimer: 0,
  _burstTimers: [],
  _driftTimer: 0,
  _anchor: null,
  _chip: null,
  _gestureArmed: false,
  _gestureHandler: null,

  init() {
    // The toggle lives in the layout, so every page render can replace it.
    // Bind whatever is in the document now; a stale node would take the click
    // and paint into a detached element.
    this.bindChip()
    if (this._inited) return
    this._inited = true
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") this.onVisible()
    })
  },

  bindChip() {
    const chip = document.getElementById("campaign-audio-chip")
    if (chip === this._chip) return
    this._chip = chip
    chip?.addEventListener("click", (e) => {
      e.preventDefault()
      this.onChip()
    })
    this.paintChip()
  },

  attach(lv) {
    this.init()
    clearTimeout(this._stopTimer)
    const rebound = this.lv !== lv
    this.lv = lv
    if (rebound && this.session) this.schedulePings(false)
  },

  detach(lv) {
    if (this.lv !== lv) return
    this.lv = null
    this.clearPingTimers()
    clearTimeout(this._stopTimer)
    this._stopTimer = window.setTimeout(() => {
      if (this.lv) return
      const slug = publicEventSlug(window.location.pathname)
      if (!this.session || slug !== this.session.slug) this.clearSession()
    }, 50)
  },

  setSession(next) {
    this.init()
    if (!next?.url || !next.slug) {
      this.clearSession()
      return
    }
    const same =
      this.session &&
      this.session.slug === next.slug &&
      this.session.url === next.url &&
      String(this.session.version) === String(next.version)
    this.session = next
    this.muted = readMuted(next.slug)
    this.applyGain()
    if (!this.unlocked) this.armGestureUnlock()
    this.paintChip()
    if (same) {
      if (this.unlocked && this.buffer) this.ensurePlaying()
      return
    }
    this.gen += 1
    const gen = this.gen
    this.stopSource()
    this.buffer = null
    this.bytes = null
    this.loadKey = ""
    this.schedulePings(true)
    this.fetchBytes().then(() => {
      if (this.gen !== gen) return
      if (this.unlocked) this.readyThenPlay()
    })
  },

  clearSession() {
    this.gen += 1
    this.disarmGestureUnlock()
    this.stopSource()
    this.session = null
    this.buffer = null
    this.bytes = null
    this.loadKey = ""
    this.playing = false
    this.unlocked = false
    this.samples = []
    this.clockOffset = null
    this.clearPingTimers()
    this.paintChip()
  },

  // Call from a real user gesture (a tap). `unlocked` means "the browser has
  // let us make sound", not "someone asked". Claiming it after a resume that
  // stayed suspended left the chip showing "muted" while nothing played — the
  // AR auto-start path (sessionStorage) has no gesture, and that is why the
  // tune started only sometimes.
  unlock() {
    this.init()
    this.ensureContext()
    this.claimAudioSession()
    this.paintChip()
    const play = () => this.readyThenPlay()
    const resumed = this.ctx?.resume?.()

    if (resumed && typeof resumed.then === "function") {
      resumed.then(
        () => {
          this.unlocked = this.ctx?.state === "running"
          this.paintChip()
          play()
        },
        () => {
          this.paintChip()
        }
      )
    } else {
      this.unlocked = this.ctx?.state === "running"
      this.paintChip()
      play()
    }

    // If the browser kept the context suspended (no gesture, or iOS refusing
    // until a real touch), the silence must not be reported as "playing". The
    // chip's `waiting` state is derived from `unlocked`, so a false positive
    // here is what made the tune look randomly on or off.
    if (!this.unlocked) this.armGestureUnlock()
  },

  // After a gesture that then awaited a permission prompt: iOS often suspends
  // the context in between. Believe `ctx.state`, not the flag we set earlier.
  syncAfterGesture() {
    this.ensureContext()
    if (this.ctx?.state === "running") {
      this.unlocked = true
      this.disarmGestureUnlock()
      this.readyThenPlay()
    } else {
      this.unlocked = false
      this.armGestureUnlock()
    }
    this.paintChip()
  },

  // The first real touch anywhere on the page is a gesture. AR auto-start has
  // none, so hang this until it fires. One pair of listeners at a time: the
  // handler is stored so `disarmGestureUnlock` can remove it, and a re-arm
  // (mosaic → AR) replaces rather than stacks.
  armGestureUnlock() {
    if (this._gestureArmed) return
    this._gestureArmed = true

    const once = () => {
      this.disarmGestureUnlock()
      this.unlock()
    }

    this._gestureHandler = once
    window.addEventListener("pointerdown", once, true)
    window.addEventListener("keydown", once, true)
  },

  disarmGestureUnlock() {
    if (!this._gestureHandler) {
      this._gestureArmed = false
      return
    }
    window.removeEventListener("pointerdown", this._gestureHandler, true)
    window.removeEventListener("keydown", this._gestureHandler, true)
    this._gestureHandler = null
    this._gestureArmed = false
  },

  async readyThenPlay() {
    const gen = this.gen
    if (!this.bytes) await this.fetchBytes()
    if (this.gen !== gen) return
    await this.decodeBytes()
    if (this.gen !== gen) return
    this.ensurePlaying()
  },

  onChip() {
    if (!this.session) return
    this.ensureContext()
    // A tap is the only gesture that can heal a stale session category, and a
    // fan tapping the chip to "unmute" is exactly the moment they expect sound.
    this.claimAudioSession({recover: true})
    const resumed = this.ctx?.resume?.()
    const after = () => {
      this.unlocked = this.ctx?.state === "running"
      if (!this.unlocked) {
        this.armGestureUnlock()
        this.paintChip()
        return
      }
      if (!this.playing) {
        // A fresh start must honour the persisted mute: `readyThenPlay` begins
        // at gain 0 when muted, so a chip reading "Unmute campaign music" here
        // would start silenced and stay labelled that way — tap, hear nothing.
        // The label is the user's instruction, so the mute it names is the one
        // that goes away before playback.
        if (this.muted) {
          this.muted = false
          writeMuted(this.session.slug, false)
        }
        this.applyGain()
        this.readyThenPlay()
      } else {
        this.muted = !this.muted
        writeMuted(this.session.slug, this.muted)
        this.applyGain()
      }
      this.paintChip()
    }
    if (resumed && typeof resumed.then === "function") resumed.then(after, after)
    else after()
  },

  // Campaign music is playback audio: it must stay audible with the Ring/Silent
  // switch on silent, and it must survive a GPU-process relaunch that drops the
  // session's cached category (WebKit bugs 323104 and the mute-switch family).
  // Both fixes are no-ops where `navigator.audioSession` does not exist.
  claimAudioSession({recover = false} = {}) {
    const session = navigator.audioSession
    if (!session) return
    if (recover) {
      // A stale category is only cleared by a real change: cycling both ways
      // forces the SetCategory through. This is the documented workaround.
      recoverPlaybackSession(session)
      return
    }
    assertPlayback(session)
  },

  // Called when playback is supposed to be audible but the context says it is
  // running. The engine can render into a muted session while every API
  // reports success, so re-assert the category once per resume.
  reassertAudioSession() {
    const session = navigator.audioSession
    if (!session) return
    cyclePlaybackSession(session)
  },

  ensureContext() {
    if (this.ctx) return this.ctx
    this.ctx = newContext()
    if (!this.ctx) return null
    this.gain = this.ctx.createGain()
    this.gain.gain.value = this.muted ? 0 : 1
    this.gain.connect(this.ctx.destination)
    return this.ctx
  },

  applyGain() {
    if (!this.gain || !this.ctx) return
    this.gain.gain.setValueAtTime(this.muted ? 0 : 1, this.ctx.currentTime)
  },

  async fetchBytes() {
    const session = this.session
    if (!session?.url) return
    const key = `${session.url}#${session.version}`
    if (this.loadKey === key && this.bytes) return
    this.loadKey = key
    try {
      const res = await fetch(mediaUrl(session.url, session.version), {cache: "force-cache"})
      if (!res.ok) throw new Error(`audio ${res.status}`)
      const bytes = await res.arrayBuffer()
      if (this.loadKey !== key) return
      this.bytes = bytes
    } catch (err) {
      if (this.loadKey === key) this.loadKey = ""
      console.warn("campaign audio fetch failed", err)
    }
  },

  async decodeBytes() {
    if (this.buffer || !this.bytes) return
    this.ensureContext()
    if (!this.ctx) return
    try {
      this.buffer = await decodeAudioData(this.ctx, this.bytes)
    } catch (err) {
      console.warn("campaign audio decode failed", err)
    }
  },

  ensurePlaying() {
    if (!this.session || !this.buffer) return
    this.ensureContext()
    if (!this.ctx) return
    if (this.ctx.state === "suspended") {
      this.ctx.resume().then(() => {
        if (this.unlocked && this.buffer && !this.playing) this.startAtClock()
      }).catch(() => {})
      return
    }
    if (this.source && this.playing) return
    this.startAtClock()
  },

  startAtClock() {
    if (!this.ctx || !this.buffer || !this.session) return
    this.stopSource()
    const duration = this.buffer.duration
    if (!(duration > 0)) return
    let pos = (this.positionMs() / 1000) % duration
    if (pos < 0 || pos >= duration) pos = 0
    const when = this.ctx.currentTime + 0.04
    const src = this.ctx.createBufferSource()
    src.buffer = this.buffer
    src.loop = true
    src.connect(this.gain)
    src.start(when, pos)
    this.source = src
    this.playing = true
    this._anchor = {ctxTime: when, offset: pos, duration}
    this.watchDrift()
    this.paintChip()
  },

  stopSource() {
    if (this.source) {
      try {
        this.source.stop()
      } catch {
        /* already stopped */
      }
      try {
        this.source.disconnect()
      } catch {
        /* ignore */
      }
    }
    this.source = null
    this.playing = false
    this._anchor = null
    if (this._driftTimer) {
      clearInterval(this._driftTimer)
      this._driftTimer = 0
    }
  },

  positionMs() {
    const duration = this.buffer
      ? Math.round(this.buffer.duration * 1000)
      : Number(this.session?.duration_ms) || 0
    return positionMs(this.serverNowMs(), duration)
  },

  serverNowMs() {
    if (this.clockOffset == null) return Date.now()
    return Math.round(this.clockOffset + performance.now())
  },

  schedulePings(burst) {
    this.clearPingTimers()
    if (!this.lv) return
    if (burst) {
      for (const wait of [0, 180, 480, 960]) {
        const id = window.setTimeout(() => this.ping(), wait)
        this._burstTimers.push(id)
      }
    } else {
      this.ping()
    }
    this._pingTimer = window.setInterval(() => this.ping(), PING_PERIOD_MS)
  },

  clearPingTimers() {
    for (const id of this._burstTimers) clearTimeout(id)
    this._burstTimers = []
    if (this._pingTimer) {
      clearInterval(this._pingTimer)
      this._pingTimer = 0
    }
  },

  ping() {
    if (!this.lv) return
    const t0 = performance.now()
    this.lv.pushEvent("clock_ping", {}, (reply) => {
      const t1 = performance.now()
      const unix = Number(reply?.unix_ms)
      if (!Number.isFinite(unix)) return
      const rtt = t1 - t0
      if (rtt > 400) return
      this.samples.push({offset: unix + rtt / 2 - t1, rtt})
      if (this.samples.length > 8) this.samples.shift()
      const ranked = [...this.samples].sort((a, b) => a.offset - b.offset)
      this.clockOffset = ranked[Math.floor(ranked.length / 2)].offset
    })
  },

  onVisible() {
    if (!this.session) return
    const resumed = this.ctx?.resume?.()
    // Coming back from the background is the other moment the engine forgets.
    this.claimAudioSession({recover: true})
    this.schedulePings(true)
    const seek = () => {
      if ((this.unlocked || this.playing) && this.ctx?.state === "running" && this.buffer) {
        this.startAtClock()
      }
    }
    if (resumed && typeof resumed.then === "function") resumed.then(seek, seek)
    else seek()
  },

  watchDrift() {
    if (this._driftTimer) clearInterval(this._driftTimer)
    this._driftTimer = window.setInterval(() => this.correctDrift(), 2000)
  },

  correctDrift() {
    if (!this.source || !this._anchor || !this.ctx || !this.buffer) return
    const duration = this._anchor.duration
    const elapsed = (this.ctx.currentTime - this._anchor.ctxTime) * (this.source.playbackRate.value || 1)
    const actual = (this._anchor.offset + elapsed) % duration
    const expected = (this.positionMs() / 1000) % duration
    const err = wrapError(expected - actual, duration)
    const abs = Math.abs(err)
    if (abs < DRIFT_OK_S) {
      this.source.playbackRate.value = 1
      return
    }
    if (abs < DRIFT_NUDGE_S) {
      this.source.playbackRate.value = err > 0 ? 1.012 : 0.988
      return
    }
    this.startAtClock()
  },

  paintChip() {
    const chip = document.getElementById("campaign-audio-chip") || this._chip
    if (!chip) return
    this._chip = chip
    const view = chipView({
      session: this.session,
      unlocked: this.unlocked,
      muted: this.muted,
      playing: this.playing,
      ctxState: this.ctx?.state,
      gestureArmed: this._gestureArmed,
    })
    chip.hidden = !view.show
    chip.classList.toggle("is-muted", view.muted)
    chip.setAttribute("aria-pressed", view.pressed ? "true" : "false")
    chip.setAttribute("aria-label", view.label)
  },
}

export const CampaignAudioSync = {
  mounted() {
    CampaignAudio.attach(this)
    CampaignAudio.setSession(readSession(this.el.dataset))
  },
  updated() {
    CampaignAudio.attach(this)
    CampaignAudio.setSession(readSession(this.el.dataset))
  },
  destroyed() {
    CampaignAudio.detach(this)
  },
}

function readSession(ds) {
  if (!ds.audioUrl || !ds.slug) return null
  return {
    slug: ds.slug,
    url: ds.audioUrl,
    duration_ms: Number(ds.audioDurationMs) || 0,
    version: ds.audioVersion || "1",
    surface: ds.audioSurface || "ar",
  }
}

export {CampaignAudio}
